import BadgeRating from 'components/BadgeRating';
import type { FindPlayer } from 'components/Find/types';
import VerificationBadge from 'components/VerificationBadge';
import classNames from 'styles/utils/classNames';

/** Only the identity fields — a preview can build these without a full FindPlayer. */
export type PlayerHeaderPlayer = Pick<
  FindPlayer,
  'name' | 'imageUrl' | 'city' | 'skillLevel' | 'sport' | 'verificationLevel'
>;

interface Props {
  player: PlayerHeaderPlayer;
  /**
   * Figma's "Modal M" sizing from lg up: `md` grows the type (the request form's
   * player card), `lg` grows the 120px avatar and badge with it (the details modal).
   */
  sizeDesktop?: 'md' | 'lg';
}

/**
 * Large identity block: 80px avatar, name, city, skill badge. Heads the player
 * details modal and the request-to-play form's player card.
 */
export default function PlayerHeader({ player, sizeDesktop }: Props) {
  const isLargeAtDesktop = sizeDesktop === 'lg';

  return (
    <div className="flex items-center gap-ds-md">
      <div
        className={classNames(
          'relative h-20 w-20 shrink-0',
          isLargeAtDesktop && 'lg:h-[7.5rem] lg:w-[7.5rem]',
        )}
      >
        <img
          src={player.imageUrl}
          alt={player.name}
          className="h-full w-full rounded-full object-cover"
        />

        <VerificationBadge
          level={player.verificationLevel}
          className={classNames('size-5', isLargeAtDesktop && 'lg:size-6')}
        />
      </div>

      <div className="flex min-w-0 flex-1 flex-col items-start gap-ds-xs">
        <h2
          className={classNames(
            'typography-product-heading-mobile truncate text-color-text-lightmode-primary dark:text-color-text-darkmode-primary',
            sizeDesktop && 'lg:typography-product-heading-desktop',
          )}
        >
          {player.name}
        </h2>

        {player.city && (
          <span
            className={classNames(
              'typography-product-text-card-mobile w-full truncate text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary',
              sizeDesktop && 'lg:typography-product-text-card-desktop',
            )}
          >
            {player.city}
          </span>
        )}

        {player.skillLevel && (
          <BadgeRating
            level={player.skillLevel}
            size="xs"
            sizeDesktop={isLargeAtDesktop ? 'sm' : undefined}
            variant="neutral"
            sport={player.sport}
          />
        )}
      </div>
    </div>
  );
}
